"use client";

import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, MessageSquare, CircleCheck as CheckCircle2, Clock, Sparkles, Zap, ShieldCheck, Building2, ArrowRight, SlidersHorizontal, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RangeInput } from "@/components/ui/range-input";
import { Link } from "@/components/common/Link";
type Language = 'es' | 'en' | 'eu' | 'fr';
const LANGUAGE_STORAGE_KEY = 'wvc_language';
const LANGUAGE_EVENT_NAME = 'wvc-language-change';
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
        return stored;
      }
    } catch {
      // fallback
    }
    return "es";
  });
  useEffect(() => {
    const handleLanguageChange = (e: Event) => {
      try {
        const customEvent = e as CustomEvent<Language>;
        if (customEvent.detail && ["es", "en", "eu", "fr"].includes(customEvent.detail)) {
          setLanguageState(customEvent.detail);
        } else {
          const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
          if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
            setLanguageState(stored);
          }
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
    window.addEventListener("storage", handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, []);
  const setLanguage = (lang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      setLanguageState(lang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: lang
      }));
    } catch {
      setLanguageState(lang);
    }
  };
  return [language, setLanguage];
}
interface ServiceOption {
  id: string;
  baseMin: number;
  baseMax: number;
  multiplierPerSqm: number;
}
const SERVICES_BASE: ServiceOption[] = [{
  id: "loxone",
  baseMin: 4500,
  baseMax: 12000,
  multiplierPerSqm: 85
}, {
  id: "reforma",
  baseMin: 2800,
  baseMax: 6500,
  multiplierPerSqm: 42
}, {
  id: "cuadro",
  baseMin: 850,
  baseMax: 2200,
  multiplierPerSqm: 5
}, {
  id: "iluminacion",
  baseMin: 1500,
  baseMax: 4800,
  multiplierPerSqm: 28
}, {
  id: "ev",
  baseMin: 1100,
  baseMax: 2400,
  multiplierPerSqm: 0
}];
const TIMELINE_FACTORS: Record<string, number> = {
  urgente: 1.15,
  normal: 1.0,
  sin_prisa: 0.95
};
const translations = {
  es: {
    eyebrow: "ESTIMADOR DE PRESUPUESTO EN LÍNEA // PAÍS VASCO",
    heading: "¿Cuánto puede costar tu proyecto?",
    description: "Calcula una horquilla técnica aproximada para instalaciones eléctricas integrales, legalizaciones y domótica Loxone en Bilbao, sus alrededores y en todo el País Vasco.",
    freeVisit: "Visita física gratuita",
    stepHeading: "1. Selecciona el tipo de trabajo",
    stepSubtitle: "Configuración según especificaciones técnicas",
    services: {
      loxone: {
        name: "Domótica Loxone Integral",
        description: "Control inteligente de iluminación, clima, persianas y seguridad"
      },
      reforma: {
        name: "Reforma Eléctrica Completa",
        description: "Cableado nuevo, mecanismos de gama alta, cuadro y legalización"
      },
      cuadro: {
        name: "Cuadro Eléctrico y Legalización",
        description: "Renovación de protecciones, rearmado automático y boletín CIE"
      },
      iluminacion: {
        name: "Iluminación LED Arquitectónica",
        description: "Fosos de furgón, tiras de perfil integradas y furgón DALI/DMX"
      },
      ev: {
        name: "Punto de Carga EV",
        description: "Cargador inteligente dinámico con balanceo de potencia"
      }
    },
    sqmLabel: "Superficie aproximada de la vivienda o local",
    captionSmall: "40 m² (Piso / Estudio)",
    captionMedium: "150 m² (Piso grande)",
    captionLarge: "350+ m² (Chalet)",
    timelineLabel: "Plazo estimado de ejecución",
    timelines: {
      urgente: "Urgente (< 1 semana)",
      normal: "Normal (1-2 semanas)",
      sin_prisa: "Sin prisa (1 mes)"
    },
    disclaimer: "Horquilla orientativa sin IVA. El precio final se confirma tras una visita y un presupuesto detallado.",
    directEstimateBadge: "ESTIMACIÓN DIRECTA",
    resultTitle: "Presupuesto Orientativo",
    calculatedRange: "Horquilla aproximada calculada",
    baseVatNote: "Base sin IVA // Sujeto a inspección in situ",
    bullet1: "Instalación eléctrica certificada por técnicos electricistas expertos",
    bullet2: "Material homologado y mecanismos oficiales",
    bullet3: "Desplazamiento y toma de datos en Bizkaia y todo el País Vasco gratis",
    whatsappBtn: "Enviar por WhatsApp",
    formBtn: "Rellenar formulario",
    bannerBadge: "Instalaciones Loxone Certificadas",
    bannerTitle: "Hablemos de tu proyecto",
    bannerText: "¿Tienes planos o memoria técnica? Enviánosla directamente y nuestro equipo evaluará los requerimientos específicos.",
    bannerCall: "Llamar directamente: 662 49 88 28",
    guarantee1Title: "Instalador Autorizado",
    guarantee1Desc: "Empresa registrada en Industria para emisión oficial de boletines y CIE en Euskadi.",
    guarantee2Title: "Partner Oficial Loxone",
    guarantee2Desc: "Integración experta de automatización domótica en viviendas de alto nivel.",
    guarantee3Title: "Respuesta Urgente 24/7",
    guarantee3Desc: "Atención rápida ante averías y reformas eléctricas con compromiso de plazos.",
    whatsappMsgPrefix: "Hola, quiero pedir presupuesto:",
    whatsappMsgSurface: "para superficie/ámbito de",
    whatsappMsgTimeline: "plazo:",
    whatsappMsgEst: "Estimación web:"
  },
  en: {
    eyebrow: "ONLINE BUDGET ESTIMATOR // BASQUE COUNTRY",
    heading: "How much could your project cost?",
    description: "Calculate an approximate technical estimate for comprehensive electrical installations, certifications, and Loxone home automation in Bilbao, surrounding areas, and throughout the Basque Country.",
    freeVisit: "Free on-site visit",
    stepHeading: "1. Select the type of work",
    stepSubtitle: "Configuration according to technical specifications",
    services: {
      loxone: {
        name: "Full Loxone Home Automation",
        description: "Intelligent control of lighting, climate, blinds, and security"
      },
      reforma: {
        name: "Complete Electrical Renovation",
        description: "New wiring, premium mechanisms, distribution board, and certification"
      },
      cuadro: {
        name: "Electrical Panel & Certification",
        description: "Renewal of circuit breakers, auto-reclosing, and CIE official compliance bulletin"
      },
      iluminacion: {
        name: "Architectural LED Lighting",
        description: "Cove lighting, integrated profile strips, and DALI/DMX dimming"
      },
      ev: {
        name: "EV Charging Station",
        description: "Smart dynamic charger with automatic power balancing"
      }
    },
    sqmLabel: "Approximate surface area of property or premises",
    captionSmall: "40 m² (Apartment / Studio)",
    captionMedium: "150 m² (Large apartment)",
    captionLarge: "350+ m² (Villa)",
    timelineLabel: "Estimated turnaround time",
    timelines: {
      urgente: "Urgent (< 1 week)",
      normal: "Standard (1-2 weeks)",
      sin_prisa: "No rush (1 month)"
    },
    disclaimer: "Indicative price range excluding VAT. Final price is confirmed after a site visit and detailed quote.",
    directEstimateBadge: "DIRECT ESTIMATE",
    resultTitle: "Indicative Budget",
    calculatedRange: "Calculated approximate range",
    baseVatNote: "Base excluding VAT // Subject to on-site inspection",
    bullet1: "Electrical installation certified by expert certified electricians",
    bullet2: "Approved materials and official brand mechanisms",
    bullet3: "Free site survey and travel across Bizkaia and the Basque Country",
    whatsappBtn: "Send via WhatsApp",
    formBtn: "Fill out contact form",
    bannerBadge: "Certified Loxone Installations",
    bannerTitle: "Let's discuss your project",
    bannerText: "Do you have architectural blueprints or technical plans? Send them directly and our team will evaluate the specifications.",
    bannerCall: "Call directly: 662 49 88 28",
    guarantee1Title: "Authorized Installer",
    guarantee1Desc: "Registered industry company issuing official compliance certificates and CIE across Euskadi.",
    guarantee2Title: "Official Loxone Partner",
    guarantee2Desc: "Expert smart home automation integration in high-end residences.",
    guarantee3Title: "24/7 Urgent Response",
    guarantee3Desc: "Fast assistance for electrical faults and renovations with firm deadline commitments.",
    whatsappMsgPrefix: "Hello, I would like to request a quote:",
    whatsappMsgSurface: "for an area of",
    whatsappMsgTimeline: "timeline:",
    whatsappMsgEst: "Online estimate:"
  },
  eu: {
    eyebrow: "ONLINEKO AURREKONTU ESTIMATZAILEA // EUSKAL HERRIA",
    heading: "Zenbat kosta daiteke zure proiektua?",
    description: "Kalkulatu gutxi gorabeherako tarte teknikoa instalazio elektriko integraletarako, legeztatzeko eta Loxone domotikarako Bilbon eta Euskal Herri osoan.",
    freeVisit: "Doako bisita presentziala",
    stepHeading: "1. Hautatu lan mota",
    stepSubtitle: "Zehaztapen teknikoen araberako konfigurazioa",
    services: {
      loxone: {
        name: "Loxone Domotika Integrala",
        description: "Argiztapenaren, klimaren, pertsianen eta segurtasunaren kontrol adimenduna"
      },
      reforma: {
        name: "Erreforma Elektriko Osoa",
        description: "Kableatu berria, goi-mailako mekanismoak, koadroa eta legeztatzea"
      },
      cuadro: {
        name: "Koadro Elektrikoa eta Legeztatzea",
        description: "Babesak berritzea, berrezarpen automatikoa eta CIE boletin ofiziala"
      },
      iluminacion: {
        name: "Arkitektura LED Argiztapena",
        description: "Fosoak, profil integratuen zerrendak eta DALI/DMX erregulazioa"
      },
      ev: {
        name: "Ibilgailu Elektrikoaren Karga-Puntua",
        description: "Kargagailu adimendun dinamikoa potentzia-orekatzearekin"
      }
    },
    sqmLabel: "Etxebizitzaren edo lokalaren gutxi gorabeherako azalera",
    captionSmall: "40 m² (Pisua / Estudioa)",
    captionMedium: "150 m² (Pisu handia)",
    captionLarge: "350+ m² (Txaleta)",
    timelineLabel: "Egiteko aurreikusitako epea",
    timelines: {
      urgente: "Premiazkoa (< astebete 1)",
      normal: "Normala (1-2 aste)",
      sin_prisa: "Presarik gabe (hilabete 1)"
    },
    disclaimer: "Orientazio-tartea BEZik gabe. Azken prezioa bisita baten eta aurrekontu zehatz baten ondoren baieztatzen da.",
    directEstimateBadge: "ZUZENEKO KALKULUA",
    resultTitle: "Orientazioko Aurrekontua",
    calculatedRange: "Kalkulatutako gutxi gorabeherako tartea",
    baseVatNote: "BEZik gabeko oinarria // Bertan egindako ikuskapenaren menpe",
    bullet1: "Teknikari elektrizista adituek ziurtatutako instalazio elektrikoa",
    bullet2: "Homologatutako materiala eta mekanismo ofizialak",
    bullet3: "Doako joan-etorria eta datu-bilketa Bizkaian eta Euskal Herri osoan",
    whatsappBtn: "WhatsApp bidez bidali",
    formBtn: "Inprimakia bete",
    bannerBadge: "Loxone Instalazio Ziurtatuak",
    bannerTitle: "Hitz egin dezagun zure proiektuaz",
    bannerText: "Planoak edo memoria teknikoa al dituzu? Bidali zuzenean eta gure taldeak zehaztapen zehatzak aztertuko ditu.",
    bannerCall: "Deitu zuzenean: 662 49 88 28",
    guarantee1Title: "Instalatzaile Baimendua",
    guarantee1Desc: "Industrian erregistratutako enpresa, Euskadin boletinak eta CIE ofizialak jaulkitzeko.",
    guarantee2Title: "Loxone Bazkide Ofiziala",
    guarantee2Desc: "Maila handiko etxebizitzetan domotika-automatizazioaren integrazio aditua.",
    guarantee3Title: "Premiazko Erantzuna 24/7",
    guarantee3Desc: "Aparailu eta matxurei arreta azkarra, epeak betetzeko konpromisoarekin.",
    whatsappMsgPrefix: "Kaixo, aurrekontua eskatu nahi nuke:",
    whatsappMsgSurface: "azalera honetarako:",
    whatsappMsgTimeline: "epea:",
    whatsappMsgEst: "Web estimazioa:"
  },
  fr: {
    eyebrow: "ESTIMATEUR DE DEVIS EN LIGNE // PAYS BASQUE",
    heading: "Combien peut coûter votre projet ?",
    description: "Calculez une fourchette technique approximative pour vos installations électriques complètes, mises aux normes et domotique Loxone à Bilbao et au Pays Basque.",
    freeVisit: "Visite physique gratuite",
    stepHeading: "1. Sélectionnez le type de travaux",
    stepSubtitle: "Configuration selon spécifications techniques",
    services: {
      loxone: {
        name: "Domotique Loxone Intégrale",
        description: "Contrôle intelligent de l'éclairage, du climat, des volets et de la sécurité"
      },
      reforma: {
        name: "Rénovation Électrique Complète",
        description: "Nouveau câblage, appareillage haut de gamme, tableau et conformité"
      },
      cuadro: {
        name: "Tableau Électrique et Conformité",
        description: "Rénovation des protections, réarmement automatique et attestation CIE"
      },
      iluminacion: {
        name: "Éclairage LED Architectural",
        description: "Gorges lumineuses, profilés intégrés et gestion DALI/DMX"
      },
      ev: {
        name: "Borne de Recharge VE",
        description: "Chargeur intelligent dynamique avec délestage de puissance"
      }
    },
    sqmLabel: "Surface approximative du logement ou du local",
    captionSmall: "40 m² (Appartement / Studio)",
    captionMedium: "150 m² (Grand appartement)",
    captionLarge: "350+ m² (Villa)",
    timelineLabel: "Délai d'exécution estimé",
    timelines: {
      urgente: "Urgent (< 1 semaine)",
      normal: "Normal (1-2 semaines)",
      sin_prisa: "Sans urgence (1 mois)"
    },
    disclaimer: "Fourchette indicative hors TVA. Le prix définitif est confirmé après visite et devis détaillé.",
    directEstimateBadge: "ESTIMATION DIRECTE",
    resultTitle: "Budget Indicatif",
    calculatedRange: "Fourchette approximative calculée",
    baseVatNote: "Base hors TVA // Sous réserve de visite sur place",
    bullet1: "Installation électrique certifiée par des électriciens experts",
    bullet2: "Matériel homologué et appareillages officiels",
    bullet3: "Déplacement et prise de mesures gratuits en Biscaye et au Pays Basque",
    whatsappBtn: "Envoyer par WhatsApp",
    formBtn: "Remplir le formulaire",
    bannerBadge: "Installations Loxone Certifiées",
    bannerTitle: "Parlons de votre projet",
    bannerText: "Vous avez des plans ou un cahier des charges ? Envoyez-les-nous et notre équipe étudiera vos besoins spécifiques.",
    bannerCall: "Appeler directement : 662 49 88 28",
    guarantee1Title: "Installateur Agréé",
    guarantee1Desc: "Entreprise enregistrée auprès de l'Industrie pour la délivrance des certificats officiels CIE.",
    guarantee2Title: "Partenaire Officiel Loxone",
    guarantee2Desc: "Intégration domotique experte pour habitations et résidences haut de gamme.",
    guarantee3Title: "Intervention Urgente 24/7",
    guarantee3Desc: "Prise en charge rapide des pannes et rénovations avec respect des délais.",
    whatsappMsgPrefix: "Bonjour, je souhaite demander un devis :",
    whatsappMsgSurface: "pour une surface de",
    whatsappMsgTimeline: "délai :",
    whatsappMsgEst: "Estimation web :"
  }
};
export default function Presupuesto() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const [selectedServiceId, setSelectedServiceId] = useState<string>("loxone");
  const [squareMeters, setSquareMeters] = useState<number>(110);
  const [timelineId, setTimelineId] = useState<string>("sin_prisa");
  const currentServiceBase = useMemo(() => {
    return SERVICES_BASE.find(s => s.id === selectedServiceId) || SERVICES_BASE[0];
  }, [selectedServiceId]);
  const serviceName = t.services[selectedServiceId as keyof typeof t.services]?.name || t.services.loxone.name;
  const timelineLabel = t.timelines[timelineId as keyof typeof t.timelines] || t.timelines.sin_prisa;
  const timelineFactor = TIMELINE_FACTORS[timelineId] ?? 0.95;
  const {
    minPrice,
    maxPrice
  } = useMemo(() => {
    const sqmFactor = currentServiceBase.multiplierPerSqm * squareMeters;
    const rawMin = (currentServiceBase.baseMin + sqmFactor) * timelineFactor;
    const rawMax = (currentServiceBase.baseMax + sqmFactor * 1.35) * timelineFactor;
    const roundedMin = Math.round(rawMin / 100) * 100;
    const roundedMax = Math.round(rawMax / 100) * 100;
    return {
      minPrice: roundedMin,
      maxPrice: roundedMax
    };
  }, [currentServiceBase, squareMeters, timelineFactor]);
  const whatsappMessage = useMemo(() => {
    const text = `${t.whatsappMsgPrefix} ${serviceName} ${t.whatsappMsgSurface} ${squareMeters} m², ${t.whatsappMsgTimeline} ${timelineLabel}. ${t.whatsappMsgEst} ${minPrice.toLocaleString("es-ES")}–${maxPrice.toLocaleString("es-ES")} €.`;
    return `https://wa.me/34662498828?text=${encodeURIComponent(text)}`;
  }, [t, serviceName, squareMeters, timelineLabel, minPrice, maxPrice]);
  return <section id="cuánto-puede-costar-tu-proyecto" data-nav="dark" className="relative w-full bg-[hsl(229,52%,8%)] text-[hsl(210,40%,98%)] overflow-hidden py-16 md:py-24 border-t border-white/10">
      {/* Background Decorative Tech Grid & Ambient Glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[hsl(217,91%,60%)]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-40" />
      </div>

      <div className="relative mx-auto max-w-[1904px] px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-12 text-center md:text-left md:flex md:items-end md:justify-between border-b border-white/10 pb-8 gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/80 border border-[hsl(217,91%,60%)]/30 text-[hsl(217,91%,60%)] text-xs font-mono mb-4">
                <span className="size-2 rounded-full bg-[hsl(217,91%,60%)] animate-pulse" />
                {t.eyebrow}
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-sans">
                {t.heading}
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                {t.description}
              </p>
            </div>

            <div className="mt-6 md:mt-0 flex items-center gap-3">
              <span className="text-xs font-mono text-[hsl(217,91%,60%)] bg-[hsl(220,26%,10%)] border border-white/10 px-3 py-2 rounded-lg inline-flex items-center gap-2">
                <ShieldCheck className="size-4" /> {t.freeVisit}
              </span>
            </div>
          </div>

          {/* Calculator Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Interactive Calculator Controls (7 cols) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="lg:col-span-7 bg-[hsl(220,26%,10%)] border border-white/10 rounded-xl p-6 sm:p-8 shadow-2xl relative">
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-[hsl(219,24%,14%)] border border-white/10 flex items-center justify-center text-[hsl(217,91%,60%)]">
                    <Calculator className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground font-sans">{t.stepHeading}</h3>
                    <p className="text-xs text-muted-foreground">{t.stepSubtitle}</p>
                  </div>
                </div>
                <SlidersHorizontal className="size-5 text-muted-foreground hidden sm:block" />
              </div>

              {/* Service Selection Radio Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {SERVICES_BASE.map((service, index) => {
                const isSelected = service.id === selectedServiceId;
                const itemInfo = t.services[service.id as keyof typeof t.services] || t.services.loxone;
                return <Button key={service.id} data-index={index} type="button" variant="ghost" onClick={() => setSelectedServiceId(service.id)} className={`text-left px-5 py-4 rounded-full border transition-all duration-200 cursor-pointer flex flex-col justify-between h-auto whitespace-normal font-normal ${isSelected ? "bg-[hsl(219,24%,18%)] border-[hsl(217,91%,60%)] ring-1 ring-[hsl(217,91%,60%)] shadow-[0_0_15px_rgba(59,130,246,0.2)]" : "bg-[hsl(219,24%,14%)]/50 border-white/10 hover:border-white/20 hover:bg-[hsl(219,24%,14%)]"}`}>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-sm text-foreground">{itemInfo.name}</span>
                          {isSelected && <CheckCircle2 className="size-4 text-[hsl(217,91%,60%)] shrink-0" />}
                        </div>
                        <p className="text-xs text-muted-foreground leading-snug line-clamp-2">
                          {itemInfo.description}
                        </p>
                      </div>
                    </Button>;
              })}
              </div>

              {/* Range Slider for Square Meters (if applicable) */}
              {currentServiceBase.multiplierPerSqm > 0 && <div className="mb-8 bg-[hsl(219,24%,14%)]/60 border border-white/10 rounded-xl p-5">
                  <div className="flex justify-between items-center mb-3">
                    <label htmlFor="sqm-range" className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Building2 className="size-4 text-[hsl(217,91%,60%)]" />
                      {t.sqmLabel}
                    </label>
                    <span className="font-mono font-bold text-lg text-[hsl(217,91%,60%)] bg-[hsl(229,52%,8%)] px-3 py-1 rounded border border-white/10">
                      {squareMeters} m²
                    </span>
                  </div>

                  <RangeInput id="sqm-range" min={40} max={350} step={5} value={squareMeters} onValueChange={val => setSquareMeters(val)} className="accent-[hsl(217,91%,60%)] my-4" />

                  <div className="flex justify-between text-xs text-muted-foreground font-mono">
                    <span>{t.captionSmall}</span>
                    <span>{t.captionMedium}</span>
                    <span>{t.captionLarge}</span>
                  </div>
                </div>}

              {/* Timeline Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Clock className="size-4 text-[hsl(217,91%,60%)]" />
                  {t.timelineLabel}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {Object.keys(TIMELINE_FACTORS).map((id, index) => {
                  const label = t.timelines[id as keyof typeof t.timelines] || id;
                  return <Button key={id} data-index={index} type="button" variant="ghost" onClick={() => setTimelineId(id)} className={`px-3 py-2.5 rounded-full border text-xs font-medium text-center transition-all cursor-pointer h-auto ${id === timelineId ? "bg-[hsl(217,91%,60%)]/15 border-[hsl(217,91%,60%)] text-[hsl(217,91%,60%)]" : "bg-[hsl(219,24%,14%)] border-white/10 text-muted-foreground hover:text-foreground"}`}>
                        {label}
                      </Button>;
                })}
                </div>
              </div>

              {/* Exact Verbatim Disclaimer Text */}
              <div className="p-4 rounded-lg bg-[hsl(229,52%,8%)] border border-white/10 text-xs text-muted-foreground flex gap-3 items-start">
                <FileText className="size-4 text-[hsl(217,91%,60%)] shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                  {t.disclaimer}
                </p>
              </div>
            </motion.div>

            {/* Results & Call-to-Actions (5 cols) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.15
          }} className="lg:col-span-5 flex flex-col gap-6">
              {/* Calculated Price Display Card */}
              <Card className="bg-[hsl(220,26%,10%)] border-2 border-[hsl(217,91%,60%)]/40 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-[hsl(217,91%,60%)] text-[hsl(222,29%,6%)] font-mono text-[10px] uppercase font-bold tracking-wider px-3 py-1 rounded-bl-lg">
                  {t.directEstimateBadge}
                </div>

                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="size-4 text-[hsl(217,91%,60%)]" />
                    {t.resultTitle}
                  </CardTitle>
                  <CardDescription className="text-foreground text-base font-medium">
                    {serviceName}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Dynamic Big Figure */}
                  <div className="py-4 px-5 rounded-xl bg-[hsl(229,52%,8%)] border border-white/10 text-center">
                    <span className="block text-xs text-muted-foreground mb-1 font-mono">{t.calculatedRange}</span>
                    <div className="font-mono text-3xl sm:text-4xl font-extrabold text-[hsl(217,91%,60%)] tracking-tight">
                      {minPrice.toLocaleString("es-ES")} € – {maxPrice.toLocaleString("es-ES")} €
                    </div>
                    <span className="text-[11px] text-muted-foreground mt-1 block">{t.baseVatNote}</span>
                  </div>

                  {/* Feature Breakdown List */}
                  <div className="space-y-2.5 text-xs text-foreground/90 font-sans">
                    <div className="flex items-center gap-2.5">
                      <Zap className="size-4 text-[hsl(217,91%,60%)] shrink-0" />
                      <span>{t.bullet1}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Zap className="size-4 text-[hsl(217,91%,60%)] shrink-0" />
                      <span>{t.bullet2}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Zap className="size-4 text-[hsl(217,91%,60%)] shrink-0" />
                      <span>{t.bullet3}</span>
                    </div>
                  </div>

                  {/* Actions / Buttons */}
                  <div className="pt-2 space-y-3">
                    {/* Primary WhatsApp Button */}
                    <Button asChild className="w-full bg-[hsl(147,64%,49%)] hover:bg-[hsl(147,64%,55%)] text-[hsl(222,29%,6%)] font-bold py-6 rounded-full transition-all flex items-center justify-center gap-2 text-base">
                      <Link to={whatsappMessage} newTab>
                        <MessageSquare className="size-5 fill-current" />
                        {t.whatsappBtn}
                      </Link>
                    </Button>

                    {/* Secondary Contact Form Action */}
                    <Button asChild variant="secondary" className="w-full bg-[hsl(219,24%,14%)] hover:bg-white/10 border border-white/15 text-foreground font-semibold py-6 rounded-full transition-all flex items-center justify-center gap-2 text-sm">
                      <Link to="#contacto">
                        <FileText className="size-4 text-[hsl(217,91%,60%)]" />
                        {t.formBtn}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Technical Showcase Image Banner */}
              <div className="relative rounded-xl overflow-hidden border border-white/10 group min-h-[220px] flex items-end p-6 bg-slate-900">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/photo-1663811397207-418a92396ad5_3441724fb1.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/photo-1663811397207-418a92396ad5_3441724fb1.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/photo-1663811397207-418a92396ad5_3441724fb1.jpg-wvc-sizes" alt="Cuadro eléctrico domótico Loxone cableado profesional en Bilbao" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-40 group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(229,52%,8%)] via-[hsl(229,52%,8%)]/60 to-transparent" />

                <div className="relative z-10 space-y-2">
                  <div className="inline-flex items-center gap-2 text-[11px] font-mono text-[hsl(217,91%,60%)] uppercase">
                    <Building2 className="size-3.5" /> {t.bannerBadge}
                  </div>
                  <h4 className="text-lg font-bold text-foreground">{t.bannerTitle}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t.bannerText}
                  </p>
                  <div className="pt-1">
                    <Link to="tel:662498828" className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-[hsl(217,91%,60%)] hover:underline">
                      {t.bannerCall}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Guarantees Grid below estimator */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-white/10 pt-10">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-[hsl(220,26%,10%)]/50 border border-white/5">
              <div className="size-10 rounded-lg bg-[hsl(219,24%,14%)] border border-white/10 flex items-center justify-center shrink-0 text-[hsl(217,91%,60%)]">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-foreground">{t.guarantee1Title}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {t.guarantee1Desc}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-[hsl(220,26%,10%)]/50 border border-white/5">
              <div className="size-10 rounded-lg bg-[hsl(219,24%,14%)] border border-white/10 flex items-center justify-center shrink-0 text-[hsl(217,91%,60%)]">
                <Sparkles className="size-5" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-foreground">{t.guarantee2Title}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {t.guarantee2Desc}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-[hsl(220,26%,10%)]/50 border border-white/5">
              <div className="size-10 rounded-lg bg-[hsl(219,24%,14%)] border border-white/10 flex items-center justify-center shrink-0 text-[hsl(217,91%,60%)]">
                <Clock className="size-5" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-foreground">{t.guarantee3Title}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {t.guarantee3Desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}